import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Reports.css';

const Reports = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [activeReport, setActiveReport] = useState('members');
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    const reports = [
        {
            id: 'members',
            title: t('Members Report'),
            description: t('View all member statistics including registrations, status changes, and demographics'),
            icon: '👥'
        },
        {
            id: 'contributions',
            title: t('Contributions Report'),
            description: t('Monthly contribution summaries, collection rates, and payment status'),
            icon: '💰'
        },
        {
            id: 'loans',
            title: t('Loans Report'),
            description: t('Loan portfolio overview, disbursements, recoveries, and default rates'),
            icon: '🏦'
        },
        {
            id: 'financial',
            title: t('Financial Summary'),
            description: t('Complete financial overview with income, expenditure, and balances'),
            icon: '📊'
        }
    ];

    const generateReport = async (reportId) => {
        setActiveReport(reportId);
        setLoading(true);
        
        try {
            switch (reportId) {
                case 'members':
                    const members = await api.getMembers({ per_page: 1000 });
                    setReportData(members.data);
                    break;
                case 'contributions':
                    const contribs = await api.getMonthlyContributionsSummary(year);
                    setReportData(contribs.data);
                    break;
                case 'loans':
                    const loans = await api.getLoansSummary();
                    setReportData(loans.data);
                    break;
                default:
                    setReportData(null);
            }
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        // TODO: Implement Excel export
        alert(t('Excel export feature coming soon'));
    };

    const exportToPDF = () => {
        // TODO: Implement PDF export
        alert(t('PDF export feature coming soon'));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    return (
        <div className="reports-page">
            <div className="page-header">
                <h1 className="page-title">{t('Reports & Analytics')}</h1>
                <div className="page-actions">
                    <button className="btn btn-secondary" onClick={exportToExcel}>
                        📊 {t('Excel')}
                    </button>
                    <button className="btn btn-secondary" onClick={exportToPDF}>
                        📄 {t('PDF')}
                    </button>
                </div>
            </div>

            {/* Year Filter */}
            <div className="filters-section">
                <div className="filter-group">
                    <label>{t('Year:')}</label>
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                        {[2020, 2021, 2022, 2023, 2024, 2025].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Report Selection */}
            <div className="reports-grid">
                {reports.map(report => (
                    <div 
                        key={report.id} 
                        className={`report-card ${activeReport === report.id ? 'active' : ''}`}
                        onClick={() => generateReport(report.id)}
                    >
                        <div className="report-icon">{report.icon}</div>
                        <h3 className="report-title">{report.title}</h3>
                        <p className="report-description">{report.description}</p>
                    </div>
                ))}
            </div>

            {/* Report Preview */}
            {activeReport && (
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            {reports.find(r => r.id === activeReport)?.title}
                        </h3>
                        <div className="card-actions">
                            <button className="btn btn-sm btn-secondary" onClick={exportToExcel}>
                                {t('Export Excel')}
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={exportToPDF}>
                                {t('Export PDF')}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading">
                            <div className="loading-spinner"></div>
                        </div>
                    ) : reportData ? (
                        <div className="report-preview">
                            {activeReport === 'members' && (
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>{t('Member #')}</th>
                                                <th>{t('Name')}</th>
                                                <th>{t('Phone')}</th>
                                                <th>{t('Status')}</th>
                                                <th>{t('Joined')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.slice(0, 20).map((member) => (
                                                <tr key={member.id}>
                                                    <td>{member.member_number}</td>
                                                    <td>{member.first_name} {member.last_name}</td>
                                                    <td>{member.phone}</td>
                                                    <td>{member.membership_status}</td>
                                                    <td>{new Date(member.membership_date).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeReport === 'contributions' && (
                                <div className="stats-grid">
                                    {reportData.map((item, index) => (
                                        <div key={index} className="stat-card">
                                            <div className="stat-label">
                                                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][item.period_month - 1]}
                                            </div>
                                            <div className="stat-value">{formatCurrency(item.total_amount)}</div>
                                            <div className="stat-label">{item.completed} {t('contributions')}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {activeReport === 'loans' && (
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>{t('Status')}</th>
                                                <th>{t('Count')}</th>
                                                <th>{t('Total Principal')}</th>
                                                <th>{t('Total Balance')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.summary_by_status?.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.status}</td>
                                                    <td>{item.count}</td>
                                                    <td>{formatCurrency(item.total_principal)}</td>
                                                    <td>{formatCurrency(item.balance)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>{t('Select a report to generate')}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Reports;
